import type { PermissionModule } from '@/hooks/usePermissions';
import type { MenuItem } from './sidebarConfig';

/**
 * Permission requise pour afficher une entrée de premier niveau du menu
 * (clé = titre). Absente = toujours visible (Accueil, Tutoriels). Accueil reste
 * visible pour tous : sans permission `reports`, /dashboard rend UserHub (menu
 * neutre sans chiffre d'affaires) au lieu du dashboard KPI (cf guards.tsx).
 * Les sections « Fichiers » n'ont pas de permission globale : ce sont leurs
 * sous-entrées qui sont filtrées via SUBITEM_PERMISSION_MAP.
 */
export const MENU_PERMISSION_MAP: Record<string, PermissionModule> = {
  'Écran de caisse': 'pos_screen',
  'Ticket W': 'pos_screen',
  'Tablette serveur': 'pos_screen',
  'Caisse rapide': 'pos_screen',
  Monnayeur: 'pos_screen',
  Balance: 'pos_screen',
  Restaurant: 'pos_screen',
  Magasin: 'pos_screen',
  Stocks: 'stock_management',
  Fidélité: 'loyalty',
  'Magic SMS': 'marketing',
  Marketing: 'marketing',
  Rapports: 'reports',
  Finance: 'finance',
  'Gestion fiscal': 'finance',
  Equipe: 'team_management',
  Website: 'settings',
  Configuration: 'settings',
  Démarrage: 'settings',
  Certification: 'settings',
  'SPEED BT': 'settings',
};

/** Permission requise par sous-entrée (clé = url). Absente = suit la section. */
export const SUBITEM_PERMISSION_MAP: Record<string, PermissionModule> = {
  '/produits': 'product_management',
  '/integrer': 'product_management',
  '/etiquettes': 'product_management',
  '/clients': 'customers',
  '/fournisseurs': 'reorder',
};

/**
 * Filtre le menu latéral selon les permissions effectives de l'utilisateur
 * (RPC get_user_permissions : défauts du rôle + overrides user_permissions).
 * Appelé uniquement pour les rôles non-admin ; les admins voient tout.
 * Une section dont toutes les sous-entrées sont refusées disparaît.
 */
export const filterMenuByPermissions = (
  items: MenuItem[],
  can: (module: PermissionModule) => boolean,
): MenuItem[] =>
  items.flatMap((item) => {
    const sectionPerm = MENU_PERMISSION_MAP[item.title];
    if (sectionPerm && !can(sectionPerm)) return [];
    if (!item.children) return [item];
    const children = item.children.filter((child) => {
      const perm = SUBITEM_PERMISSION_MAP[child.url];
      return !perm || can(perm);
    });
    if (children.length === 0) return [];
    return [children.length === item.children.length ? item : { ...item, children }];
  });

type PathEntry = { readonly url: string; readonly perm: PermissionModule | null };

const menuEntries = (items: MenuItem[]): PathEntry[] =>
  items.flatMap((item) => {
    const sectionPerm = MENU_PERMISSION_MAP[item.title] ?? null;
    const children = [
      ...(item.children ?? []),
      ...(item.sections ?? []).flatMap((s) => s.children),
    ];
    return [
      ...(item.url ? [{ url: item.url, perm: sectionPerm }] : []),
      ...children.map((c) => ({ url: c.url, perm: SUBITEM_PERMISSION_MAP[c.url] ?? sectionPerm })),
    ];
  });

/**
 * Permissions qui ouvrent une page, déduites du MÊME menu que la barre latérale
 * (garde de routes, flag `garde_routes_permissions`) : menu et garde ne divergent jamais.
 * Entrée la plus précise (url exacte ou préfixe « url/ »). Une page présente à plusieurs
 * endroits du menu est autorisée si l'une des permissions l'est.
 * `null` = page hors menu ou entrée sans permission → autorisée.
 */
export const requiredPermissionsForPath = (
  path: string,
  items: MenuItem[],
): PermissionModule[] | null => {
  const matches = menuEntries(items).filter(
    (e) => path === e.url || path.startsWith(e.url.endsWith('/') ? e.url : `${e.url}/`),
  );
  if (matches.length === 0) return null;
  const best = Math.max(...matches.map((m) => m.url.length));
  const top = matches.filter((m) => m.url.length === best);
  if (top.some((m) => m.perm === null)) return null;
  return [...new Set(top.map((m) => m.perm as PermissionModule))];
};
